'use client';

import { useState, useRef, useEffect } from 'react';
import {
  Mic, MicOff, Play, Square, Loader2, Wand2, AudioLines,
  ChevronDown, ChevronUp, RefreshCw, Sparkles, Bookmark, BookmarkCheck, Trash2, Upload,
} from 'lucide-react';
import VoiceDesigner from './VoiceDesigner';

interface VoiceProfile {
  id: number;
  voicebox_profile_id: string | null;
  sample_audio_path: string | null;
  instruct_text: string | null;
  voice_seed: number | null;
  status: string;
}

interface VoiceLine {
  id: number;
  character_id: number;
  audio_path: string;
  label: string | null;
  created_at: string;
}

interface Props {
  characterId: string;
  characterName: string;
  voiceProfile: VoiceProfile | null;
  servicesOnline: { voicebox: boolean; rvc: boolean };
}

const statusLabels: Record<string, { label: string; color: string; note?: string }> = {
  designing:      { label: 'No voice yet',               color: 'text-slate-500'  },
  generating:     { label: 'Generating training audio…', color: 'text-amber-400'  },
  ready_to_train: { label: 'Ready to train',             color: 'text-blue-400'   },
  queued:         { label: 'Queued for overnight training', color: 'text-sky-400', note: 'Training will start automatically between 10pm–6:30am' },
  training:       { label: 'Training in progress…',      color: 'text-amber-400'  },
  trained:        { label: 'Voice ready',                color: 'text-emerald-400'},
  failed:         { label: 'Something went wrong',       color: 'text-red-400'    },
};

export default function CharacterVoicePanel({
  characterId, characterName, voiceProfile, servicesOnline,
}: Props) {
  const [expanded, setExpanded] = useState(false);
  const [activeProfileId] = useState(voiceProfile?.voicebox_profile_id ?? '');
  const [ttsText, setTtsText] = useState('');
  const [ttsAudio, setTtsAudio] = useState<string | null>(null);
  const [converting, setConverting] = useState(false);
  const [conversionAudio, setConversionAudio] = useState<string | null>(null);
  const [conversionPath, setConversionPath] = useState<string | null>(null);
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // ── Recorder ──
  const [recording, setRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState<Blob | null>(null);
  const [recordedUrl, setRecordedUrl] = useState<string | null>(null);
  const [recordSeconds, setRecordSeconds] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── Save-line state ──
  const [lineLabel, setLineLabel] = useState('');
  const [savingLine, setSavingLine] = useState(false);
  const [lineSaved, setLineSaved] = useState(false);

  // ── Voice library ──
  const [savedLines, setSavedLines] = useState<VoiceLine[]>([]);

  // ── Demo line (TTS → RVC pipeline) ──
  const [demoAudio, setDemoAudio] = useState<string | null>(null);

  // ── Design preview audio (for trained state) ──
  const [previewAudio, setPreviewAudio] = useState<string | null>(
    voiceProfile?.sample_audio_path ? `/${voiceProfile.sample_audio_path}` : null
  );

  // ── Training status ──
  const [currentStatus, setCurrentStatus] = useState(voiceProfile?.status ?? 'designing');
  const status = currentStatus;
  const statusInfo = statusLabels[status] ?? statusLabels.designing;
  const hasTrained = status === 'trained';
  const hasVoiceboxProfile = !!activeProfileId || !!voiceProfile?.instruct_text;

  // Poll training completion every 2 min (also poll when queued to detect when scheduler starts it)
  useEffect(() => {
    if (status !== 'training' && status !== 'queued') return;
    const interval = setInterval(async () => {
      try {
        const res = await fetch(`/api/voice/training-status?characterId=${characterId}`);
        if (!res.ok) return;
        const { status: s } = await res.json();
        if (s !== status) setCurrentStatus(s);
      } catch {}
    }, 120_000);
    return () => clearInterval(interval);
  }, [status, characterId]);

  // Load saved lines when panel expands
  useEffect(() => {
    if (!expanded || !hasTrained) return;
    fetch(`/api/voice/lines?characterId=${characterId}`)
      .then(r => r.json())
      .then(setSavedLines)
      .catch(() => {});
  }, [expanded, hasTrained, characterId]);

  // ── Handlers ──

  async function handlePreview() {
    setLoading('preview'); setError(null);
    try {
      if (activeProfileId) {
        // Cloning workflow
        const res = await fetch('/api/voice/preview', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ characterId, voiceboxProfileId: activeProfileId }),
        });
        if (!res.ok) throw new Error(await res.text());
        const { audioPath } = await res.json();
        setPreviewAudio(`/${audioPath}`);
      } else {
        // Design workflow — use TTS with a short sample phrase
        const res = await fetch('/api/voice/tts', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ characterId, text: `Hello, I'm ${characterName}. What do you want?` }),
        });
        if (!res.ok) throw new Error(await res.text());
        const { audioPath } = await res.json();
        setPreviewAudio(`/${audioPath}`);
      }
    } catch (e: any) { setError(e.message); }
    finally { setLoading(null); }
  }

  async function handleGenerateTraining() {
    setLoading('train-gen'); setError(null);
    try {
      const res = await fetch('/api/voice/generate-training', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ characterId }),
      });
      if (!res.ok) throw new Error(await res.text());
      window.location.reload();
    } catch (e: any) { setError(e.message); }
    finally { setLoading(null); }
  }

  async function handleTTS() {
    if (!ttsText.trim()) return;
    setLoading('tts'); setError(null);
    try {
      const res = await fetch('/api/voice/tts', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ characterId, text: ttsText }),
      });
      if (!res.ok) throw new Error(await res.text());
      const { audioPath } = await res.json();
      setTtsAudio(`/${audioPath}`);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(null); }
  }

  // One-click demo: TTS → RVC to hear the trained character voice
  async function handleDemo() {
    setLoading('demo'); setError(null); setDemoAudio(null);
    try {
      const ttsRes = await fetch('/api/voice/tts', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ characterId, text: `Hello, I'm ${characterName}. What do you want?` }),
      });
      if (!ttsRes.ok) throw new Error(await ttsRes.text());
      const { audioPath } = await ttsRes.json();

      const audioRes = await fetch(`/${audioPath}`);
      const audioBlob = await audioRes.blob();
      const fd = new FormData();
      fd.append('file', new File([audioBlob], 'demo.wav', { type: 'audio/wav' }));
      fd.append('characterId', characterId);
      const convertRes = await fetch('/api/voice/convert', { method: 'POST', body: fd });
      if (!convertRes.ok) throw new Error(await convertRes.text());
      const { audioPath: convertedPath } = await convertRes.json();
      setDemoAudio(`/${convertedPath}`);
    } catch (e: any) { setError(e.message); }
    finally { setLoading(null); }
  }

  async function startRecording() {
    setError(null); setRecordedBlob(null); setRecordedUrl(null);
    setConversionAudio(null); setConversionPath(null); setLineSaved(false);
    chunksRef.current = [];
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream);
      mediaRecorderRef.current = recorder;
      recorder.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      recorder.onstop = () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        setRecordedBlob(blob);
        setRecordedUrl(URL.createObjectURL(blob));
      };
      recorder.start();
      setRecording(true); setRecordSeconds(0);
      timerRef.current = setInterval(() => setRecordSeconds(s => s + 1), 1000);
    } catch {
      setError('Microphone access denied — check your browser settings, or use the Upload option below.');
    }
  }

  function stopRecording() {
    mediaRecorderRef.current?.stop();
    setRecording(false);
    if (timerRef.current) clearInterval(timerRef.current);
  }

  function handleFileUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null); setConversionAudio(null); setConversionPath(null); setLineSaved(false);
    setRecordedBlob(file);
    setRecordedUrl(URL.createObjectURL(file));
    e.target.value = '';
  }

  async function handleConversion(file: File) {
    setConverting(true); setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('characterId', characterId);
      const res = await fetch('/api/voice/convert', { method: 'POST', body: fd });
      if (!res.ok) throw new Error(await res.text());
      const { audioPath } = await res.json();
      setConversionAudio(`/${audioPath}`);
      setConversionPath(audioPath);
    } catch (e: any) { setError(e.message); }
    finally { setConverting(false); }
  }

  async function handleSaveLine() {
    if (!conversionPath) return;
    setSavingLine(true);
    try {
      await fetch('/api/voice/save-line', {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ characterId, audioPath: conversionPath, label: lineLabel }),
      });
      setLineSaved(true);
      const res = await fetch(`/api/voice/lines?characterId=${characterId}`);
      setSavedLines(await res.json());
    } catch (e: any) { setError(e.message); }
    finally { setSavingLine(false); }
  }

  async function handleDeleteLine(id: number) {
    await fetch(`/api/voice/lines?id=${id}`, { method: 'DELETE' });
    setSavedLines(lines => lines.filter(l => l.id !== id));
  }

  function resetRecorder() {
    setConversionAudio(null); setConversionPath(null);
    setRecordedUrl(null); setRecordedBlob(null);
    setLineSaved(false); setLineLabel('');
  }

  return (
    <div className="bg-slate-900 border border-slate-800 rounded-xl overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between p-4 hover:bg-slate-800/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <AudioLines size={18} className="text-purple-400" />
          <span className="font-bold text-sm">Voice Studio</span>
          <span className={`text-xs ${statusInfo.color}`}>{statusInfo.label}</span>
        </div>
        {expanded ? <ChevronUp size={16} className="text-slate-500" /> : <ChevronDown size={16} className="text-slate-500" />}
      </button>

      {expanded && (
        <div className="border-t border-slate-800 p-4 space-y-5">

          {status === 'queued' && (
            <div className="text-xs text-sky-400 bg-sky-900/20 border border-sky-500/20 rounded-lg px-3 py-2 space-y-1">
              <p className="font-semibold">Training audio is ready!</p>
              <p className="text-sky-300/70">{statusInfo.note}</p>
            </div>
          )}

          {!servicesOnline.voicebox && status !== 'queued' && status !== 'training' && (
            <p className="text-xs text-amber-400 bg-amber-900/20 border border-amber-500/20 rounded-lg px-3 py-2">
              Voicebox is offline. Start it on Civilbuddy to use voice features.
            </p>
          )}
          {error && (
            <p className="text-xs text-red-400 bg-red-900/20 border border-red-500/20 rounded-lg px-3 py-2">{error}</p>
          )}

          {/* ── Trained: Voice preview ── */}
          {hasTrained && (
            <div className="space-y-2 pb-3 border-b border-slate-800">
              <p className="text-xs font-mono uppercase tracking-widest text-slate-500">Voice Design Preview</p>
              <p className="text-xs text-slate-400">
                The designed voice that {characterName} was trained on.
              </p>

              {previewAudio && (
                <audio controls src={previewAudio} className="w-full h-8" />
              )}

              <div className="flex flex-wrap gap-2">
                <button
                  onClick={handlePreview}
                  disabled={loading === 'preview' || !servicesOnline.voicebox}
                  className="flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-40 border border-slate-700 rounded-lg text-sm transition-colors"
                >
                  {loading === 'preview' ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                  {previewAudio ? 'Regenerate' : 'Preview designed voice'}
                </button>

                {servicesOnline.rvc && (
                  <button
                    onClick={handleDemo}
                    disabled={loading === 'demo' || !servicesOnline.voicebox}
                    className="flex items-center gap-2 px-3 py-2 bg-purple-800 hover:bg-purple-700 disabled:opacity-40 border border-purple-600/40 rounded-lg text-sm font-bold transition-colors"
                  >
                    {loading === 'demo' ? <><Loader2 size={14} className="animate-spin" /> Generating…</> : <><Sparkles size={14} /> Hear trained voice</>}
                  </button>
                )}
              </div>

              {loading === 'demo' && (
                <p className="text-xs text-slate-500">Generating a line with Voicebox then converting with RVC — takes ~20 seconds…</p>
              )}

              {demoAudio && (
                <div className="space-y-1">
                  <p className="text-xs text-emerald-400 font-semibold">This is {characterName}&apos;s trained voice:</p>
                  <audio controls src={demoAudio} className="w-full h-8" />
                </div>
              )}
            </div>
          )}

          {/* Step 1 — Design (hidden while training is running or queued) */}
          {status !== 'training' && status !== 'queued' && <div className="space-y-2">
            <p className="text-xs font-mono uppercase tracking-widest text-slate-500">
              {hasTrained ? 'Voice Design' : 'Step 1 — Design the Voice'}
            </p>
            <VoiceDesigner
              characterId={characterId}
              characterName={characterName}
              savedInstruct={voiceProfile?.instruct_text ?? null}
              savedSeed={voiceProfile?.voice_seed ?? null}
              voiceboxOnline={servicesOnline.voicebox}
              onDesignSaved={() => window.location.reload()}
            />
          </div>}

          {/* Step 2 — Preview (both cloning and design workflows) */}
          {hasVoiceboxProfile && !hasTrained && status !== 'training' && status !== 'queued' && (
            <div className="space-y-2">
              <p className="text-xs font-mono uppercase tracking-widest text-slate-500">Step 2 — Preview Voice</p>
              <div className="flex gap-2 flex-wrap">
                <button
                  onClick={handlePreview}
                  disabled={loading === 'preview' || !servicesOnline.voicebox}
                  className="flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-40 border border-slate-700 rounded-lg text-sm transition-colors"
                >
                  {loading === 'preview' ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
                  Hear the voice
                </button>
                {previewAudio && (
                  <button onClick={handlePreview} className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-300">
                    <RefreshCw size={12} /> Try different
                  </button>
                )}
              </div>
              {previewAudio && (
                <audio controls src={previewAudio} className="w-full h-8 mt-1" />
              )}
            </div>
          )}

          {/* Step 3 — Train */}
          {hasVoiceboxProfile && status !== 'trained' && (
            <div className="space-y-2">
              <p className="text-xs font-mono uppercase tracking-widest text-slate-500">Step 3 — Train Voice Conversion Model</p>
              <p className="text-xs text-slate-400 leading-relaxed">
                This generates ~10 minutes of training audio, then trains an AI model so you can convert
                recorded performances into {characterName}&apos;s voice. Takes about 2 hours — you can close
                this page and come back later!
              </p>
              {status === 'training' && (
                <p className="text-xs text-amber-300 bg-amber-900/20 border border-amber-500/20 rounded-lg px-3 py-2">
                  Training is running in the background — this takes about 2 hours. This page will update automatically when it&apos;s done!
                </p>
              )}
              <button
                onClick={handleGenerateTraining}
                disabled={loading === 'train-gen' || !servicesOnline.voicebox || status === 'generating' || status === 'training' || status === 'queued'}
                className="flex items-center gap-2 px-3 py-2 bg-blue-700 hover:bg-blue-600 disabled:opacity-40 rounded-lg text-sm font-bold transition-colors"
              >
                {loading === 'train-gen' || status === 'generating' || status === 'training' || status === 'queued'
                  ? <><Loader2 size={14} className="animate-spin" /> Working…</>
                  : <><Wand2 size={14} /> Generate &amp; Train</>}
              </button>
            </div>
          )}

          {/* TTS */}
          {hasTrained && (
            <div className="space-y-2 pt-2 border-t border-slate-800">
              <p className="text-xs font-mono uppercase tracking-widest text-slate-500">Speak a Line (Text → Voice)</p>
              <textarea
                value={ttsText}
                onChange={e => setTtsText(e.target.value)}
                placeholder={`What does ${characterName} say?`}
                rows={2}
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 resize-none focus:outline-none focus:border-purple-500"
              />
              <button
                onClick={handleTTS}
                disabled={!ttsText.trim() || loading === 'tts' || !servicesOnline.voicebox}
                className="flex items-center gap-2 px-3 py-2 bg-purple-700 hover:bg-purple-600 disabled:opacity-40 rounded-lg text-sm font-bold transition-colors"
              >
                {loading === 'tts' ? <><Loader2 size={14} className="animate-spin" /> Generating…</> : <><Play size={14} /> Generate</>}
              </button>
              {ttsAudio && <audio controls src={ttsAudio} className="w-full h-8 mt-1" />}
            </div>
          )}

          {/* Perform & Convert */}
          {hasTrained && (
            <div className="space-y-3 pt-2 border-t border-slate-800">
              <p className="text-xs font-mono uppercase tracking-widest text-slate-500">Perform as {characterName}</p>
              <p className="text-xs text-slate-400 leading-relaxed">
                Act out a scene as {characterName} — speak their lines, add gasps, sighs, whatever feels right.
                Hit <span className="text-white font-bold">Convert</span> and the AI will transform your voice into {characterName}&apos;s!
              </p>

              {!servicesOnline.rvc && (
                <p className="text-xs text-amber-400 bg-amber-900/20 border border-amber-500/20 rounded-lg px-3 py-2">
                  Voice conversion is offline right now.
                </p>
              )}

              {servicesOnline.rvc && (
                <div className="space-y-3">
                  {/* Hidden file input */}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="audio/*"
                    className="hidden"
                    onChange={handleFileUpload}
                  />

                  {!recordedUrl && (
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={recording ? stopRecording : startRecording}
                        disabled={converting}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-bold transition-colors ${
                          recording
                            ? 'bg-red-600 hover:bg-red-500 animate-pulse'
                            : 'bg-slate-700 hover:bg-slate-600 border border-slate-600'
                        }`}
                      >
                        {recording ? (
                          <><Square size={14} fill="currentColor" /> Stop Recording
                            <span className="font-mono text-xs opacity-75 ml-1">
                              {Math.floor(recordSeconds / 60)}:{String(recordSeconds % 60).padStart(2, '0')}
                            </span>
                          </>
                        ) : (
                          <><Mic size={14} /> Record</>
                        )}
                      </button>

                      {!recording && (
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          disabled={converting}
                          className="flex items-center gap-2 px-4 py-2.5 bg-slate-700 hover:bg-slate-600 disabled:opacity-40 border border-slate-600 rounded-lg text-sm font-bold transition-colors"
                        >
                          <Upload size={14} /> Upload Audio File
                        </button>
                      )}
                    </div>
                  )}

                  {!recordedUrl && !recording && (
                    <p className="text-xs text-slate-600">
                      Can&apos;t use the mic? Upload an audio file (mp3, wav, m4a, etc.) instead.
                    </p>
                  )}

                  {recordedUrl && !converting && !conversionAudio && (
                    <div className="space-y-2">
                      <p className="text-xs text-slate-400">Your recording — happy with it?</p>
                      <audio controls src={recordedUrl} className="w-full h-8" />
                      <div className="flex gap-2 flex-wrap">
                        <button
                          onClick={() => handleConversion(new File([recordedBlob!], 'performance.webm', { type: recordedBlob!.type }))}
                          className="flex items-center gap-2 px-4 py-2 bg-purple-700 hover:bg-purple-600 rounded-lg text-sm font-bold transition-colors"
                        >
                          <Sparkles size={14} /> Convert to {characterName}&apos;s Voice
                        </button>
                        <button
                          onClick={() => { setRecordedUrl(null); setRecordedBlob(null); }}
                          className="flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 border border-slate-700 rounded-lg text-sm transition-colors"
                        >
                          <MicOff size={14} /> Try Again
                        </button>
                      </div>
                    </div>
                  )}

                  {converting && (
                    <div className="flex items-center gap-2 text-sm text-slate-400">
                      <Loader2 size={14} className="animate-spin text-purple-400" />
                      Converting your performance… this takes a moment!
                    </div>
                  )}

                  {conversionAudio && (
                    <div className="space-y-3 bg-emerald-900/20 border border-emerald-500/20 rounded-xl p-3">
                      <p className="text-xs font-bold text-emerald-300">That&apos;s {characterName}&apos;s voice!</p>
                      <audio controls src={conversionAudio} className="w-full h-8" />

                      {/* Save to library */}
                      {!lineSaved ? (
                        <div className="space-y-2 pt-1 border-t border-emerald-500/10">
                          <p className="text-xs text-slate-400">Love this take? Save it to {characterName}&apos;s Voice Library so you can add it to a storyboard later.</p>
                          <div className="flex gap-2">
                            <input
                              value={lineLabel}
                              onChange={e => setLineLabel(e.target.value)}
                              placeholder={`Name it (e.g. "the battle cry")`}
                              className="flex-1 bg-slate-800 border border-slate-700 rounded-lg px-3 py-1.5 text-xs text-slate-200 focus:outline-none focus:border-emerald-500 placeholder:text-slate-600"
                            />
                            <button
                              onClick={handleSaveLine}
                              disabled={savingLine}
                              className="flex items-center gap-1.5 px-3 py-1.5 bg-emerald-700 hover:bg-emerald-600 disabled:opacity-40 rounded-lg text-xs font-bold transition-colors shrink-0"
                            >
                              {savingLine ? <Loader2 size={12} className="animate-spin" /> : <Bookmark size={12} />}
                              Save
                            </button>
                          </div>
                        </div>
                      ) : (
                        <p className="text-xs text-emerald-400 flex items-center gap-1.5 pt-1 border-t border-emerald-500/10">
                          <BookmarkCheck size={13} /> Saved to {characterName}&apos;s Voice Library!
                        </p>
                      )}

                      <button
                        onClick={resetRecorder}
                        className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 transition-colors"
                      >
                        <RefreshCw size={11} /> Perform another take
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Voice Library */}
          {hasTrained && savedLines.length > 0 && (
            <div className="space-y-2 pt-2 border-t border-slate-800">
              <p className="text-xs font-mono uppercase tracking-widest text-slate-500">{characterName}&apos;s Voice Library</p>
              <div className="space-y-2">
                {savedLines.map(line => (
                  <div key={line.id} className="flex items-center gap-2 bg-slate-800/60 rounded-lg px-3 py-2">
                    <div className="flex-1 min-w-0">
                      {line.label && <p className="text-xs font-bold text-slate-300 truncate mb-1">{line.label}</p>}
                      <audio controls src={`/${line.audio_path}`} className="w-full h-7" />
                    </div>
                    <button
                      onClick={() => handleDeleteLine(line.id)}
                      className="text-slate-600 hover:text-red-400 transition-colors shrink-0 p-1"
                      title="Remove"
                    >
                      <Trash2 size={13} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  );
}
